import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getUser, logout } from '../../services/auth';
import { MdCalendarToday, MdLocationOn, MdAttachMoney } from 'react-icons/md';
import api from '../../services/api';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';

export default function AdminPanel() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, id: null }); // type: 'user' | 'job'
  const { showToast, removeToast, toasts } = useToast();
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    loadData();
  }, [activeTab, navigate, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await api.get('/users');
        setUsers(response.data || []);
      } else if (activeTab === 'jobs') {
        const response = await api.get('/jobs').catch(() => ({ data: { jobs: [] } }));
        // API returns { jobs: [...], pagination: {...} } or just array
        const jobsData = response.data?.jobs || (Array.isArray(response.data) ? response.data : []);
        setJobs(jobsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteConfirm({ isOpen: true, type: 'user', id: userId });
  };

  const confirmDeleteUser = async () => {
    const userId = deleteConfirm.id;
    if (!userId) return;
    
    try {
      await api.delete(`/users/${userId}`);
      loadData();
      showToast('Korisnik je uspešno obrisan.', 'success');
    } catch (error) {
      showToast('Greška pri brisanju korisnika: ' + error.message, 'error');
    }
  };

  const handleDeleteJob = (jobId) => {
    setDeleteConfirm({ isOpen: true, type: 'job', id: jobId });
  };

  const confirmDeleteJob = async () => {
    const jobId = deleteConfirm.id;
    if (!jobId) return;
    
    try {
      await api.delete(`/jobs/${jobId}`);
      loadData();
      showToast('Posao je uspešno obrisan.', 'success');
    } catch (error) {
      showToast('Greška pri brisanju posla: ' + error.message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/admin" className="text-primary-600 hover:text-primary-700">
              ← Nazad na Dashboard
            </Link>
            <h1 className="text-2xl font-bold gradient-bg bg-clip-text text-transparent">
              Admin Panel
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 dark:text-gray-300">
              {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              Odjavi se
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b">
        <div className="container mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-6 border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-600 text-primary-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-primary-600'
              }`}
            >
              Korisnici
            </button>
            <button
              onClick={() => setActiveTab('jobs')}
              className={`py-4 px-6 border-b-2 transition-colors ${
                activeTab === 'jobs'
                  ? 'border-primary-600 text-primary-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-primary-600'
              }`}
            >
              Poslovi
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-4 px-6 border-b-2 transition-colors ${
                activeTab === 'payments'
                  ? 'border-primary-600 text-primary-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-primary-600'
              }`}
            >
              Plaćanja
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {activeTab === 'users' && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-4">Svi korisnici</h2>
            {users.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nema korisnika.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Ime</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Uloga</th>
                      <th className="text-left py-3 px-4">Akcije</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="py-3 px-4">
                          {u.firstName} {u.lastName}
                        </td>
                        <td className="py-3 px-4">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            u.role === 'admin' ? 'bg-red-100 text-red-800' :
                            u.role === 'parent' ? 'bg-primary-100 text-primary-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-red-600 hover:text-red-800 font-semibold"
                          >
                            Obriši
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-4">Svi poslovi</h2>
            {jobs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nema poslova.</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{job.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{job.description}</p>
                        <div className="flex gap-4 mt-2 text-sm flex-wrap">
                          <span className="flex items-center gap-1 text-gray-500">
                            <MdCalendarToday className="text-base" />
                            {new Date(job.date).toLocaleDateString('sr-RS')}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <MdLocationOn className="text-base" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <MdAttachMoney className="text-base" />
                            {job.hourlyRate || 0} RSD/sat
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          job.status === 'active' ? 'bg-green-100 text-green-800' :
                          job.status === 'posted' ? 'bg-primary-100 text-primary-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="text-red-600 hover:text-red-800 font-semibold ml-4"
                        >
                          Obriši
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-4">Plaćanja</h2>
            <p className="text-gray-500 text-center py-8">
              Plaćanja funkcija će biti dodata uskoro.
            </p>
          </div>
        )}
      </main>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen && deleteConfirm.type === 'user'}
        onClose={() => setDeleteConfirm({ isOpen: false, type: null, id: null })}
        onConfirm={confirmDeleteUser}
        title="Potvrda brisanja"
        message="Da li ste sigurni da želite da obrišete ovog korisnika?"
        confirmText="Obriši"
        cancelText="Otkaži"
        type="danger"
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen && deleteConfirm.type === 'job'}
        onClose={() => setDeleteConfirm({ isOpen: false, type: null, id: null })}
        onConfirm={confirmDeleteJob}
        title="Potvrda brisanja"
        message="Da li ste sigurni da želite da obrišete ovaj posao?"
        confirmText="Obriši"
        cancelText="Otkaži"
        type="danger"
      />
    </div>
  );
}

